import { t } from '@bia-auction/shared-i18n';
import { Moon, Sun } from 'lucide-react';

import { cn } from '../lib/cn';
import { useTheme } from '../lib/use-theme';
import { Button } from './button';

export interface ThemeToggleProps {
  /**
   * Render the action text next to the icon. Off by default so the control fits
   * icon-only chrome (a collapsed sidebar); the accessible name is on the button
   * either way.
   */
  showLabel?: boolean;
  className?: string;
}

/**
 * Light/dark switch over the stored preference (docs/15 §5). Icon and label name
 * the theme the click switches **to**, not the one in effect — a moon on a light
 * page reads as "go dark", which is what the operator is reaching for.
 *
 * Routes that pin their own theme via `ThemeScope` (the login hero) are not
 * affected while mounted, so this belongs in app chrome that those routes don't
 * render.
 */
export function ThemeToggle({ showLabel = false, className }: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme();
  const dark = theme === 'dark';
  const label = dark ? t('theme.switchToLight') : t('theme.switchToDark');
  const Icon = dark ? Sun : Moon;

  return (
    <Button
      type="button"
      variant="ghost"
      size={showLabel ? 'sm' : 'icon'}
      // The label is the accessible name when it renders; `aria-label` covers the
      // icon-only form and `title` gives the same text as a hover tooltip there.
      aria-label={showLabel ? undefined : label}
      title={showLabel ? undefined : label}
      onClick={toggleTheme}
      className={cn(showLabel && 'justify-start', className)}
    >
      <Icon aria-hidden className="h-4 w-4 shrink-0" />
      {showLabel && label}
    </Button>
  );
}
